import { useNavigate } from "react-router-dom"
import { ArrowLeft, Shield, Lock, Eye, FileText, Bell, Users, RefreshCw } from "lucide-react"
import { Button } from "./ui/button"
import { Footer } from "./layout/Footer"
import { useEffect } from "react"
import SEO from "../utils/SEO"

export default function PrivacyPolicyPage() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  const handleNavigateHome = () => {
    window.scrollTo(0, 0)
    navigate('/app')
  }

  return (
    <div className="min-h-screen bg-black text-white flex flex-col">
      <SEO 
        title="Privacy Policy"
        description="Learn about how Spyfall Online collects, uses, and protects your personal information. Our commitment to your privacy and data security."
        keywords="spyfall privacy policy, data protection, online game privacy, user data security"
      />

      {/* Header */}
      <header className="sticky top-0 z-40 w-full border-b border-gray-800 bg-black/80 backdrop-blur-sm">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 flex h-16 items-center justify-between">
          <div className="flex items-center gap-2">
            <button onClick={() => window.location.replace('/')} className="flex items-center gap-2">
              <ArrowLeft className="h-4 w-4" />
              <span className="text-xl font-bold text-red-500">SPYFALL</span>
            </button>
          </div>
          <Button 
            variant="outline" 
            className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
            onClick={handleNavigateHome}
          >
            Back to Home
          </Button>
        </div>
      </header>

      <main className="flex-1 py-12">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8 flex items-center gap-3">
            <Shield className="h-8 w-8 text-red-500" />
            <h1 className="text-3xl font-bold">Privacy Policy</h1>
          </div>

          <div className="text-gray-400 mb-8 text-center">
            <p>Last updated: January 14, 2026</p>
          </div>

          <div className="prose prose-invert max-w-none">
            <p className="text-gray-300 text-center">
              At Spyfall, we take your privacy seriously. This Privacy Policy explains how we collect, use, disclose,
              and safeguard your information when you use our game application. Please read this privacy policy
              carefully. If you do not agree with the terms of this privacy policy, please do not access the
              application.
            </p>

            <div className="mt-8 space-y-12">
              {/* Information Collection */}
              <section className="bg-gray-900 rounded-lg p-6 border-l-4 border-red-500">
                <div className="flex items-center gap-3 mb-4">
                  <Eye className="h-6 w-6 text-red-400 flex-shrink-0" />
                  <h2 className="text-xl font-bold">Information Collection</h2>
                </div>
                <p className="mb-4">
                  We may collect information about you in a variety of ways. The information we may collect via the
                  Application includes:
                </p>
                <h3 className="text-lg font-semibold mb-2">Personal Data</h3>
                <p className="mb-4">
                  While using our application, we may ask you to provide us with certain personally identifiable
                  information that can be used to contact or identify you. This may include:
                </p>
                <ul className="list-disc pl-6 mb-4 space-y-1">
                  <li>Username or display name</li>
                  <li>Email address (if you choose to register)</li>
                  <li>Profile picture (optional)</li>
                </ul>

                <h3 className="text-lg font-semibold mb-2">Game Data</h3>
                <p>
                  We collect information about your gameplay, including game statistics, scores, and performance
                  metrics. This information is used to improve your gaming experience and provide features like
                  leaderboards and achievements.
                </p>
              </section>

              {/* Use of Information */}
              <section className="bg-gray-900 rounded-lg p-6 border-l-4 border-red-500">
                <div className="flex items-center gap-3 mb-4">
                  <FileText className="h-6 w-6 text-red-400 flex-shrink-0" />
                  <h2 className="text-xl font-bold">Use of Your Information</h2>
                </div>
                <p className="mb-4">
                  Having accurate information about you permits us to provide you with a smooth, efficient, and
                  customized experience. Specifically, we may use information collected about you via the Application
                  to:
                </p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Create and manage your account</li>
                  <li>Deliver targeted content and game features to you</li>
                  <li>Generate game statistics and leaderboards</li>
                  <li>Improve the Application and your user experience</li>
                  <li>Notify you of updates to the Application</li>
                  <li>Offer new products, services, and/or recommendations to you</li>
                  <li>Perform other business activities as needed</li>
                  <li>Prevent fraudulent transactions, monitor against theft, and protect against criminal activity</li>
                  <li>Request feedback and contact you about your use of the Application</li>
                </ul>
              </section>

              {/* Security of Information */}
              <section className="bg-gray-900 rounded-lg p-6 border-l-4 border-red-500">
                <div className="flex items-center gap-3 mb-4">
                  <Lock className="h-6 w-6 text-red-400 flex-shrink-0" />
                  <h2 className="text-xl font-bold">Security of Your Information</h2>
                </div>
                <p className="mb-4">
                  We use administrative, technical, and physical security measures to help protect your personal
                  information. While we have taken reasonable steps to secure the personal information you provide to
                  us, please be aware that despite our efforts, no security measures are perfect or impenetrable, and no
                  method of data transmission can be guaranteed against any interception or other type of misuse.
                </p>
                <p>
                  Any information disclosed online is vulnerable to interception and misuse by unauthorized parties.
                  Therefore, we cannot guarantee complete security if you provide personal information.
                </p>
              </section>

              {/* Third-Party Services */}
              <section className="bg-gray-900 rounded-lg p-6 border-l-4 border-red-500">
                <div className="flex items-center gap-3 mb-4">
                  <Users className="h-6 w-6 text-red-400 flex-shrink-0" />
                  <h2 className="text-xl font-bold">Third-Party Services</h2>
                </div>
                <p className="mb-4">
                  The Application may contain links to third-party websites and applications of interest, including
                  advertisements and external services, that are not affiliated with us. Once you have used these links
                  to leave the Application, any information you provide to these third parties is not covered by this
                  Privacy Policy, and we cannot guarantee the safety and privacy of your information.
                </p>
                <p>
                  Before visiting and providing any information to any third-party websites, you should inform yourself
                  of the privacy policies and practices of the third party responsible for that website, and should take
                  those steps necessary to, in your discretion, protect the privacy of your information.
                </p>
              </section>

              {/* Your Rights */}
              <section className="bg-gray-900 rounded-lg p-6 border-l-4 border-red-500">
                <div className="flex items-center gap-3 mb-4">
                  <Shield className="h-6 w-6 text-red-400 flex-shrink-0" />
                  <h2 className="text-xl font-bold">Your Rights</h2>
                </div>
                <p className="mb-4">
                  You have the right to request information about the personal data we hold about you and to request
                  correction, deletion, or restriction of your personal information. You also have the right to object
                  to processing of your personal information and the right to data portability.
                </p>
                <p>
                  If you have an account with us, you can review, update, or delete your information by logging into
                  your account and visiting your account profile page.
                </p>
              </section>

              {/* Policy Updates */}
              <section className="bg-gray-900 rounded-lg p-6 border-l-4 border-red-500">
                <div className="flex items-center gap-3 mb-4">
                  <RefreshCw className="h-6 w-6 text-red-400 flex-shrink-0" />
                  <h2 className="text-xl font-bold">Policy Updates</h2>
                </div>
                <p className="mb-4">
                  We may update this privacy policy from time to time in order to reflect changes to our practices or
                  for other operational, legal, or regulatory reasons. We will notify you of any changes by posting the
                  new policy on this page and updating the "Last updated" date.
                </p>
                <p>
                  You are advised to review this Privacy Policy periodically for any changes. Changes to this Privacy
                  Policy are effective when they are posted on this page.
                </p>
              </section>

              {/* Contact Us */}
              <section className="bg-gray-900 rounded-lg p-6 border-l-4 border-red-500">
                <div className="flex items-center gap-3 mb-4">
                  <Bell className="h-6 w-6 text-red-400 flex-shrink-0" />
                  <h2 className="text-xl font-bold">Contact Us</h2>
                </div>
                <p className="mb-4">
                  If you have questions or comments about this Privacy Policy, please contact us at:
                </p>
                <div className="bg-gray-800 p-4 rounded-md">
                  <p className="font-medium">Spyfall Game Team</p>
                  <p>Email: privacy@spyfallgame.com</p>
                  <p>Address: 123 Game Street, Suite 456, Game City, GC 12345</p>
                </div>
              </section>
            </div>
          </div>

          <div className="mt-12 flex justify-center">
            <Button 
              className="bg-red-500 hover:bg-red-600"
              onClick={handleNavigateHome}
            >
              Return to Home
            </Button>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  )
}

